<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="box">
    <b-loading :is-full-page="false" class="small" :active="loading" />
    <div v-if="!isInViewer || (isInViewer && !loading)">
      <!-- <h2>
      <template v-if="titlePrefix">{{titlePrefix}} </template>
<span :class="{'has-text-weight-bold': titlePrefix || titleSuffix}">{{title}}</span>
<template v-if="titleSuffix"> {{titleSuffix}}</template> ({{nbAnnotations}}) </h2> -->
      <template v-if="error">
        <b-message type="is-danger" has-icon icon-size="is-small">
          {{ $t('failed-fetch-annots') }}
        </b-message>
      </template>
      <template v-else-if="!annotations.length">
        <em class="no-result">{{ $t('no-annotation') }}</em>
      </template>
      <template v-else style="display:'flex'; flex-direction: column; gap: 0.5rem;">
        <template v-for="(annot, index) in annotations">
          <div class="break" v-if="regroupPerLine && annotationInGroupDetails[index].first"
            :key="((isInViewer) ? index : '') + title + annot.id + 'break-in'"></div>
          <annotation-preview :key="((isInViewer) ? index : '') + title + annot.id" :class="annotStyles(annot, index)"
            class="annot-preview-block" :annot="annot" :size="size" :color="color" :terms="allTerms" :images="allImages"
            :show-image-info="!isInViewer" @addTerm="$emit('addTerm', $event)"
            @updateTermsOrTracks="$emit('updateTermsOrTracks', annot)" @updateProperties="$emit('updateProperties')"
            @centerView="$emit('centerView', annot)" @deletion="$emit('delete', annot)"
            @select="$emit('select', $event)" />
          <div class="break" v-if="regroupPerLine && annotationInGroupDetails[index].last"
            :key="((isInViewer) ? index : '') + title + annot.id + 'break-out'"></div>
        </template>


        <cytomine-pagination :total="nbAnnotations" :current.sync="currentPage" size="is-small" :per-page="nbPerPage" />
      </template>
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/store-helpers';

import AnnotationPreview from './AnnotationPreview';
import CytominePagination from '@/components/utils/CytominePagination';

import { AnnotationCollection } from '@/api';
import constants from '@/utils/constants';
import _ from 'lodash';

export default {
  name: 'list-annotations-by',
  props: {
    categorization: String,
    nbPerPage: Number,
    size: Number,
    color: String,
    bundling: { type: String, default: 'NO' },

    prop: Object,

    multipleTerms: Boolean,
    noTerm: Boolean,

    termsIds: Array,
    imagesIds: Array,

    allTerms: Array,
    allImages: Array,

    revision: Number,
    visible: { type: Boolean, default: true },
    showDetails: { type: Boolean, default: true },
    showAlgo: { type: Boolean, default: false },
    index: String
  },
  components: { AnnotationPreview, CytominePagination },
  data() {
    return {
      loading: true,
      error: false,
      annotations: [],
      nbAnnotations: 0,
      pendingReload: true,
      savedSlice: null
    };
  },
  computed: {
    project: get('currentProject/project'),

    isByTerm() {
      return this.categorization === 'TERM';
    },
    isByImage() {
      return this.categorization === 'IMAGE';
    },
    isUncategorized() {
      return this.categorization === 'UNCATEGORIZED';
    },
    filteredTermsIds() {
      return this.termsIds.filter(id => id > 0);
    },

    tooManyImages() {
      return this.project.numberOfImages > constants.MAX_IMAGES_FOR_FILTER;
    },

    collection() {
      this.revision; // to ensure that collection is reloaded if revision changes
      return new AnnotationCollection({

        term: (this.isByTerm && !this.multipleTerms && !this.noTerm) ? this.prop.id : null,
        image: (this.isByImage) ? this.prop.id : null,

        noTerm: this.noTerm,
        multipleTerm: this.multipleTerms,

        terms: (!this.isByTerm && (this.noTerm || this.multipleTerm || this.filteredTermsIds.length < this.allTerms.length)) ? this.filteredTermsIds.filter(id => id > 0) : null,
        images: this.imagesIds,

        showTerm: true,
        showGIS: true,

        showImageGroup: true,
        showWKT: this.isInViewer,
        showImage: this.tooManyImages,

        showAlgo: this.showAlgo || null,
        max: this.nbPerPage,
        project: this.currentProject.id
      });
    },
    title() {
      if (this.isByImage) {
        return (this.prop.blindedName) ? this.prop.blindedName : this.prop.instanceFilename;
      } else if (this.isByTerm && this.multipleTerms) {
        return this.$t('multiple-terms');
      } else if (this.isByTerm && this.noTerm) {
        return this.$t('no-term');
      } else if (this.isUncategorized) {
        return this.$t('all-annotations-uncategorized');
      } else {
        return this.prop.name;
      }
    },
    titlePrefix() {
      if (!this.isInViewer) {
        return '';
      }

      let prefix = '';
      if (this.isByTerm) {
        prefix += this.$t('currently-visible-annotations-with');
        if (!this.noTerm) {
          prefix += ` ${this.$t('the-term')}`;
        }
      }

      return prefix;
    },
    titleSuffix() {
      if (!this.isInViewer) {
        return;
      } else if (this.isByTerm) {
        return this.$t('in-this-slice');
      } else {
        return this.$t('in-this-image');
      }
    },
    isInViewer() {
      return (this.index !== undefined);
    },
    currentProject: get('currentProject/project'),
    listAnnotationsState() {
      return this.$store.getters['currentProject/projectModuleState']('listAnnotations');
    },
    projectModule() {
      return this.$store.getters['currentProject/projectModulePath']('listAnnotations');
    },
    imageModule() {
      return (this.isInViewer) ? this.$store.getters['currentProject/imageModulePath'](this.index) : null;
    },
    imageWrapper() {
      return (this.isInViewer) ? this.$store.getters['currentProject/currentViewer'].images[this.index] : null;
    },
    currentPage: {
      get() {
        if (this.isInViewer) {
          return this.imageWrapper.annotationsList.currentPages[this.prop.id] || 1;
        }
        return this.listAnnotationsState?.currentPages?.[this.prop.id] || 1;
      },
      set(page) {
        if (this.isInViewer) {
          this.$store.commit(this.imageModule + 'setCurrentPage', { prop: this.prop.id, page });
        }
        this.$store.commit(this.projectModule + '/setCurrentPage', { prop: this.prop.id, page });
      }
    },
    activeSlices() {
      return (this.imageWrapper) ? this.imageWrapper.activeSlices : null;
    },
    activeSlicesIds() {
      return (this.activeSlices) ? this.activeSlices.map(slice => slice.id) : [];
    },
    activeSliceWithSmallestRank() {
      return (this.activeSlices) ? _.orderBy(this.activeSlices, ['rank'])[0] : null;
    },

    annotationIds() {
      return this.annotations.map(annot => annot.id);
    },

    regroup() {
      return this.bundling !== 'NO';
    },
    regroupPerLine() {
      return this.bundling === 'ONE_PER_LINE';
    },

    annotationInGroupDetails() {
      return this.annotations.map((annotation, index) => {
        let previousGroup = (index > 0) ? this.annotations[index - 1].group : null;
        let nextGroup = (index < this.annotations.length - 1) ? this.annotations[index + 1].group : null;
        // let annotsInGroup = annotation.annotationLink.map(al => al.annotation);
        return {
          first: annotation.group !== null && annotation.group !== previousGroup,
          last: annotation.group !== null && annotation.group !== nextGroup,
          in: annotation.group !== null,
          // complete: annotsInGroup.every(annot => this.annotationIds.includes(annot))
        };
      });
    }
  },
  watch: {
    currentPage() {
      this.fetchPage();
    },
    collection(newCollection, oldCollection) {
      this.fetchPage();
    },
    visible() {
      if (this.pendingReload) {
        this.fetchPage();
      }
    },
    activeSlices() {
      this.findPage();
    }
  },
  methods: {
    annotStyles(annot, index) {
      let groupDetails = this.annotationInGroupDetails[index];
      return {
        'active': this.isInViewer && this.activeSlicesIds.includes(annot.slice),
        'group-first': this.regroup && groupDetails.first,
        'group-last': this.regroup && groupDetails.last,
        'group-in': this.regroup && groupDetails.in && !groupDetails.first && !groupDetails.last,
        'group-complete': this.regroup && groupDetails.in && groupDetails.complete
      };
    },
    async initialize() {
      await this.findPage();
      await this.fetchPage(true);
    },
    async findPage() {
      if (this.isInViewer) {
        this.currentPage = 1;
      }
    },
    async fetchPage(setLoading = false) {
      if (!this.visible) { // prevent unnecessary reload if list not visible but schedule a reload at next opening
        this.pendingReload = true;
        return;
      }

      this.pendingReload = false;

      if ((!this.tooManyImages && this.imagesIds && !this.imagesIds.length) ||
        (this.termsIds && !this.termsIds.length)) {
        this.annotations = [];
        this.nbAnnotations = 0;
        this.loading = false;
        return;
      }

      if (setLoading) {
        this.loading = true;
      }

      try {
        let data = await this.collection.fetchPage(this.currentPage - 1);
        this.annotations = data.array;
        this.nbAnnotations = data.totalNbItems;
      } catch (error) {
        if (this.currentPage > 1) { // error may be due to the page number (not enough annots) => retry on first page
          this.currentPage = 1;
          return;
        }

        console.log(error);
        this.nbAnnotations = 0;
        this.error = true;
      }
      this.loading = false;
    },


  },
  async created() {
    await this.initialize();
    this.loading = false;
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.box {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

.no-result {
  color: $dark-text-disabled;
}

/**
 * TODO: use :deep(.class) when moving to Vue3
 */
::v-deep ul.pagination-list {
  justify-content: flex-end;
}

::v-deep .active .annot-preview {
  box-shadow: 0 2px 3px rgba(39, 120, 173, 0.75), 0 0 0 1px rgba(39, 120, 173, 0.75);
  font-weight: 600;
}

.annot-preview-block {
  margin: 4px;
  /* padding: 6px; */
  width: 50px;
  width: 50px;
}

>>>.annot-preview {
  margin: 0;
  padding: 0;
}

>>>.group-first,
.group-in,
.group-last {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  border-top: 2px dashed rgb(100, 100, 100);
  border-bottom: 2px dashed rgb(100, 100, 100);
}

>>>.group-first.group-complete,
.group-in.group-complete,
.group-last.group-complete {
  border-top-style: solid;
  border-bottom-style: solid;
}

>>>.group-first {
  padding-left: 4px !important;
  border-left: 2px dashed rgb(100, 100, 100);
  margin-right: 0 !important;
  padding-right: 10px !important;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

>>>.group-first.group-complete {
  border-left-style: solid;
}

>>>.group-last {
  padding-right: 4px !important;
  border-right: 2px dashed rgb(100, 100, 100);
  margin-left: 0 !important;
  padding-left: 10px !important;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

>>>.group-last.group-complete {
  border-right-style: solid;
}

>>>.group-first.group-last {
  margin: 4px !important;
  padding: 4px !important;
}

>>>.group-in {
  margin-left: 0 !important;
  padding-left: 10px !important;
  margin-right: 0 !important;
  padding-right: 10px !important;
}

.break {
  flex-basis: 100%;
  height: 0;
}
</style>
